import { css } from '@emotion/react';
import { useAtom } from 'jotai';
import { FC, useRef } from 'react';

import { CategoryOption, Choice, PickupLocation } from 'api/api';

import { CategoryAddressProvider } from '~src/address/search-address/address-provider';
import { useCategoryState } from '~src/category/category-state';
import { InlineExtensionList } from '~src/category/inline-extension-list';
import { openedAtom } from '~src/category/state/category-extesion-atoms';
import { constructUserChoice } from '~src/category/user-choice';
import { UserChoiceProvider } from '~src/category/user-choice-provider';
import { OpenListButtonLayout } from '~src/commons/components/button/open-list-button';
import { LogoDeck } from '~src/commons/components/logo-deck-row';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useSessionState } from '~src/global-state';
import { PriceComponent } from '~src/price/price';
import { colors } from '~src/theme/colors';
import { useAriaGridCell } from '~src/ui/aria-grid';
import { ListState, useInlineListState } from '~src/ui/list-state';
import { Separator } from '~src/ui/separator';
import { ShippingCardUI } from '~src/ui/shipping-card';
import { useIpc } from '~src/utils/ipc-utils';

import { useHasAddress } from '~address-form/utils/address-utils';

import { DistanceComponent } from './distance';
import { LocationAddress } from './location-address';
import { LocationDeliveryTime } from './location-delivery-time';
import { LocationTypeComponent } from './location-type';
import { OperationalHoursComponent } from './operational-hours';

interface InlineLocationListItemProps {
  option: CategoryOption;
  listState: ListState<CategoryOption>;
  hideCarrierLogos: boolean;
  onSelect: (choice: Choice) => void;
}

export const InlineLocationListItem: FC<InlineLocationListItemProps> = ({
  option,
  listState,
  hideCarrierLogos,
  onSelect,
}) => {
  const session = useSessionState();
  const category = useCategoryState();
  const { tICU } = useTranslations();

  const ref = useRef(null);
  const location = option.location!;

  const { cellProps, isSelected } = useAriaGridCell({
    itemKey: location.id,
    listState,
    ref,
    enableAutoSelect: false,
    onSelect: itemKey => {
      // don't submit the same location
      if (itemKey === listState.selectedKey) return;

      onSelect(constructUserChoice(category, option));
    },
  });

  return (
    <ShippingCardUI
      headerExtensions={{
        price: session.flags.show_shipping_price_on_service_points_view ? (
          <PriceComponent value={option.price} />
        ) : undefined,
        logo: <LogoDeck shippingMethods={[option.carrier_logo || option.shipping_method]} />,
      }}
      radioTitle={
        isSelected ? tICU(`widget.aria.SELECTED_PICKUP_POINT`) : tICU(`widget.aria.PICKUP_POINT`)
      }
      key={location.id}
      selected={isSelected}
      title={location.name}
      reserveLogoSpace={category.flags.show_carrier_logos && !hideCarrierLogos}
      spacing="dense"
      enablePadding
      ariaLevel={4}
      innerRef={ref}
      {...cellProps}
    >
      <LocationAddress address={location.address} />
      <LocationDeliveryTime deliveryTime={option.delivery_times[0]!} />
      <OperationalHoursComponent location={location} />
      <Separator css={{ marginTop: '4px' }}>
        <DistanceComponent distance={location.distance} />
        <LocationTypeComponent location={location} />
      </Separator>
    </ShippingCardUI>
  );
};

interface InlineLocationListProps {
  defaultOption: CategoryOption;
  options: CategoryOption[];
  onUpdate: (newChoice: Choice) => void;
}

export const InlineLocationList: FC<InlineLocationListProps> = ({
  options,
  defaultOption,
  onUpdate,
}) => {
  const listState = useInlineListState({
    items: options,
    getKey: (option: CategoryOption) => option.location!.id,
    defaultSelectedKey: defaultOption.location!.id,
  });

  const hideCarrierLogos = options.every(
    option => option.shipping_method === options[0].shipping_method
  );

  return (
    <InlineExtensionList
      listState={listState}
      getId={item => item.location!.id} // TODO check if location.id is always present
      listStyles={css({
        '&>[role=grid]>[role=row]:first-child>[role=gridcell]': {
          borderTop: 'none',
          paddingTop: '13px',
        },
      })}
    >
      {({ item, hidden }) =>
        hidden || (
          <InlineLocationListItem
            key={item.location!.id}
            option={item}
            listState={listState}
            onSelect={onUpdate}
            hideCarrierLogos={hideCarrierLogos}
          />
        )
      }
    </InlineExtensionList>
  );
};

interface InlineLocationExtensionProps {
  initialSelectedOption: CategoryOption;
}

export const InlineLocationExtension: FC<InlineLocationExtensionProps> = ({
  initialSelectedOption,
}) => {
  const ipc = useIpc();
  const { tICU } = useTranslations();
  const category = useCategoryState();
  const session = useSessionState();
  const hasAddress = useHasAddress();

  const buttonRef = useRef<HTMLDivElement>(null);
  const [, setOpened] = useAtom(openedAtom);

  if (!session.flags.enable_address_form && !initialSelectedOption.location) {
    return null;
  }

  const options = category.options.filter(option => !!option.location);

  const defaultOption = !!initialSelectedOption.location ? initialSelectedOption : options[0];

  const getLocationText = () => {
    if (category.pickup_location_types && category.pickup_location_types.length === 1) {
      switch (category.pickup_location_types[0]) {
        case 'LOCKER':
          return tICU('widget.SHIPPING_CHOICE.locker.DEFAULT_LOCATION_TEXT');
        case 'STORE':
          return tICU('widget.SHIPPING_CHOICE.instore.DEFAULT_LOCATION_TEXT');
      }
    }
    return tICU(`widget.SHIPPING_CHOICE.${category.delivery_type}.DEFAULT_LOCATION_TEXT`);
  };

  const getLocationDescription = (location?: PickupLocation) => {
    if (!location) return getLocationText();

    return (
      <>
        <span>{location.name}</span>,{' '}
        <LocationAddress address={location.address} showCity={false} />
      </>
    );
  };

  return (
    <CategoryAddressProvider>
      {({ onOpen }) => (
        <UserChoiceProvider>
          {({ onUpdate }) => (
            <OpenListButtonLayout
              innerRef={buttonRef}
              onClick={() => (hasAddress ? setOpened(prev => !prev) : onOpen())}
              title={
                hasAddress
                  ? { text: tICU('widget.PICK_UP_AT'), textStyle: 'normal', color: colors.black }
                  : undefined
              }
              description={{
                text: getLocationDescription(initialSelectedOption.location),
                textStyle: 'normal',
                color: colors.darkGrey,
              }}
              textSize={hasAddress ? 'normal' : 'small'}
            >
              <InlineLocationList
                options={options}
                defaultOption={defaultOption}
                onUpdate={newChoice => {
                  onUpdate(newChoice);
                  setOpened(false);
                  if (buttonRef.current) {
                    ipc.send({
                      name: 'request_scroll_change',
                      data: buttonRef.current.getBoundingClientRect().top,
                    });
                  }
                }}
              />
            </OpenListButtonLayout>
          )}
        </UserChoiceProvider>
      )}
    </CategoryAddressProvider>
  );
};
